import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Bookmark, ChevronRight, ArrowLeft, Calendar, Trash2 } from 'lucide-react';
import { useChampionship } from '@/contexts/ChampionshipContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import volleyballApi from '@/services/api';

export const FavoritesPage = () => {
  const navigate = useNavigate();
  const { favorites, toggleFavorite } = useChampionship();
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChampionships();
  }, [favorites]);

  const loadChampionships = async () => {
    try {
      setLoading(true);
      const data = await volleyballApi.getChampionships();
      
      // Handle new format: {championships: [...], categoryOrder: [...]}
      const champsList = data.championships || data;
      
      // Filter only favorites
      const favoriteChamps = champsList.filter(c => favorites.includes(c.id));
      
      setChampionships(favoriteChamps);
    } catch (error) {
      console.error('Error loading championships:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChampionshipClick = (championship) => {
    navigate(`/championship/${championship.id}`);
  };

  const handleRemoveFavorite = (e, championshipId) => {
    e.stopPropagation();
    toggleFavorite(championshipId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento preferiti...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </button>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Preferiti
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {championships.length === 0 ? (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 text-center">
            <Bookmark className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Nessun preferito
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Non hai ancora salvato nessun campionato nei preferiti.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Sfoglia Campionati
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {championships.length} campionat{championships.length === 1 ? 'o' : 'i'} salvat{championships.length === 1 ? 'o' : 'i'}
            </p>
            
            <div className="space-y-3">
              {championships.map((championship) => (
                <div
                  key={championship.id}
                  onClick={() => handleChampionshipClick(championship)}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 cursor-pointer hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                        <Trophy className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                          {championship.series} {championship.gender}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Girone {championship.group} • {championship.level}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button
                        onClick={(e) => handleRemoveFavorite(e, championship.id)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full transition-colors"
                        title="Rimuovi dai preferiti"
                      >
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </button>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4">
          <div className="flex justify-around py-2">
            <button
              onClick={() => navigate('/')}
              className="flex flex-col items-center py-2 px-4 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            >
              <Trophy className="h-6 w-6" />
              <span className="text-xs mt-1">Campionati</span>
            </button>
            <button
              onClick={() => navigate('/partite')}
              className="flex flex-col items-center py-2 px-4 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            >
              <Calendar className="h-6 w-6" />
              <span className="text-xs mt-1">Partite</span>
            </button>
            <button
              className="flex flex-col items-center py-2 px-4 text-blue-600 dark:text-blue-400 relative"
            >
              <Bookmark className="h-6 w-6" />
              <span className="text-xs mt-1 font-medium">Preferiti</span>
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {favorites.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
